// src/review/canvas/sections/ResidualRiskSection.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Stack, Text, Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useReview } from '../../context/ReviewContext';
import { saveResidualRisk } from '../../../redux/slices/residualRiskSlice';
import { trackEdit } from '../../../redux/slices/reviewSlice';
import EditableField from '../shared/EditableField';

/**
 * ResidualRiskSection - Shows residual risk evaluation after controls
 * RTK-first: reads from residualRiskRTK.byAssessment, falls back to legacy
 * Keyed by assessmentUuid (not modelId)
 */
function ResidualRiskSection({ risk }) {
  const dispatch = useDispatch();
  const { assessmentUuid, modelId } = useReview();

  // RTK-first: read from RTK slice, fall back to legacy
  const residualRiskData = useSelector((state) => {
    const rtkData = state.residualRiskRTK?.byAssessment?.[assessmentUuid]?.[risk.riskId] || {};
    const legacyData = state.residualRisk?.byModel?.[assessmentUuid]?.[risk.riskId] || {};
    return { ...legacyData, ...rtkData };
  });

  const hasResidualData = residualRiskData.residual_rating || residualRiskData.residual_explanation;

  // Handlers for saving ratings
  const handleSaveResidualRating = useCallback(async (newValue) => {
    const oldValue = residualRiskData.residual_rating;
    if (newValue === oldValue) return;

    try {
      await dispatch(saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: risk.riskId,
        riskName: risk.riskName,
        priority: risk.priority,
        residual_rating: newValue,
        residual_explanation: residualRiskData.residual_explanation,
        action_plan_rating: residualRiskData.action_plan_rating || residualRiskData.actionPlanRating,
        action_plan_analysis: residualRiskData.action_plan_analysis || residualRiskData.actionPlanAnalysis,
      })).unwrap();

      dispatch(trackEdit({
        modelId,
        assessmentUuid,
        itemType: 'residual_risk',
        itemId: risk.riskId,
        field: 'residual_rating',
        oldValue,
        newValue
      }));

      notifications.show({
        title: 'Residual Risk Updated',
        message: `Changed from ${oldValue} to ${newValue}`,
        color: 'blue'
      });
    } catch (error) {
      console.error('[ResidualRiskSection] Error saving residual rating:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update residual risk rating',
        color: 'red'
      });
    }
  }, [dispatch, residualRiskData, modelId, assessmentUuid, risk]);

  const handleSaveActionPlanRating = useCallback(async (newValue) => {
    const oldValue = residualRiskData.action_plan_rating || residualRiskData.actionPlanRating;
    if (newValue === oldValue) return;

    try {
      await dispatch(saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: risk.riskId,
        riskName: risk.riskName,
        priority: risk.priority,
        residual_rating: residualRiskData.residual_rating,
        residual_explanation: residualRiskData.residual_explanation,
        action_plan_rating: newValue,
        action_plan_analysis: residualRiskData.action_plan_analysis || residualRiskData.actionPlanAnalysis,
      })).unwrap();

      dispatch(trackEdit({
        modelId,
        assessmentUuid,
        itemType: 'residual_risk',
        itemId: risk.riskId,
        field: 'action_plan_rating',
        oldValue,
        newValue
      }));

      notifications.show({
        title: 'Action Plan Updated',
        message: `Changed from ${oldValue} to ${newValue}`,
        color: 'blue'
      });
    } catch (error) {
      console.error('[ResidualRiskSection] Error saving action plan rating:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update action plan rating',
        color: 'red'
      });
    }
  }, [dispatch, residualRiskData, modelId, assessmentUuid, risk]);

  const handleSaveActionPlanAnalysis = useCallback(async (newValue, oldValue) => {
    if (newValue === oldValue) return;

    try {
      await dispatch(saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: risk.riskId,
        riskName: risk.riskName,
        priority: risk.priority,
        residual_rating: residualRiskData.residual_rating,
        residual_explanation: residualRiskData.residual_explanation,
        action_plan_rating: residualRiskData.action_plan_rating || residualRiskData.actionPlanRating,
        action_plan_analysis: newValue,
      })).unwrap();
    } catch (error) {
      console.error('[ResidualRiskSection] Error saving action plan analysis:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update action plan analysis',
        color: 'red'
      });
    }
  }, [dispatch, residualRiskData, modelId, assessmentUuid, risk]);

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Text size="md" fw={700}>Residual Risk Assessment</Text>

        {/* Residual Risk Rating Selector */}
        <Stack gap="xs">
          <Text size="sm" fw={600}>Residual Risk Rating</Text>
          <Select
            value={residualRiskData.residual_rating || ''}
            onChange={handleSaveResidualRating}
            data={[
              { value: 'Negligible', label: 'Negligible' },
              { value: 'Low', label: 'Low' },
              { value: 'Moderate', label: 'Moderate' },
              { value: 'Significant', label: 'Significant' },
              { value: 'High', label: 'High' },
              { value: 'Critical', label: 'Critical' }
            ]}
            placeholder="Select residual risk level..."
            disabled={!residualRiskData.residual_rating && !residualRiskData.residual_explanation}
            description={(!residualRiskData.residual_rating && !residualRiskData.residual_explanation) ? 'Generate residual risk assessment first' : undefined}
          />
        </Stack>

        {hasResidualData ? (
          <Stack gap="md">
            {residualRiskData.residual_explanation && (
              <EditableField
                label="Residual Risk Explanation"
                value={residualRiskData.residual_explanation}
                itemType="residual_risk"
                itemId={risk.riskId}
                fieldName="residual_explanation"
                multiline
                minRows={3}
                placeholder="Explain the residual risk after controls..."
              />
            )}

            {/* Action Plan Rating Selector */}
            {(residualRiskData.actionPlanRating || residualRiskData.action_plan_rating) && (
              <Stack gap="xs">
                <Text size="sm" fw={600}>Action Plan Effectiveness</Text>
                <Select
                  value={residualRiskData.action_plan_rating || residualRiskData.actionPlanRating || ''}
                  onChange={handleSaveActionPlanRating}
                  data={[
                    { value: 'Unsatisfactory', label: 'Unsatisfactory' },
                    { value: 'Poor', label: 'Poor' },
                    { value: 'Fair', label: 'Fair' },
                    { value: 'Good', label: 'Good' },
                    { value: 'Very Good', label: 'Very Good' },
                    { value: 'Excellent', label: 'Excellent' }
                  ]}
                  placeholder="Select action plan effectiveness..."
                  disabled={!residualRiskData.actionPlanRating && !residualRiskData.action_plan_rating}
                  description="Rate the effectiveness of the action plan"
                />
              </Stack>
            )}

            {(residualRiskData.actionPlanAnalysis || residualRiskData.action_plan_analysis) && (
              <EditableField
                label="Action Plan Analysis"
                value={residualRiskData.action_plan_analysis || residualRiskData.actionPlanAnalysis}
                itemType="residual_risk"
                itemId={risk.riskId}
                fieldName="action_plan_analysis"
                multiline
                minRows={2}
                onSave={handleSaveActionPlanAnalysis}
              />
            )}

            {residualRiskData.overridden && (
              <Card withBorder p="sm" bg="yellow.0">
                <Stack gap="xs">
                  <Text size="sm" fw={600} c="yellow.8">Manual Override</Text>
                  {residualRiskData.overridden.rating && (
                    <Text size="sm">
                      <strong>Rating:</strong> {residualRiskData.overridden.rating}
                    </Text>
                  )}
                  {residualRiskData.overridden.explanation && (
                    <Text size="sm">
                      <strong>Explanation:</strong> {residualRiskData.overridden.explanation}
                    </Text>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            No residual risk assessment available. Complete the residual risk evaluation step.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

ResidualRiskSection.propTypes = {
  risk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
  }).isRequired,
};

export default ResidualRiskSection;
